import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type AbilitiesService from 'ember-can/services/abilities';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LocationsService from 'garaje/services/locations';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type UserFeatureFlagsService from 'garaje/services/user-feature-flags';
import { AnalyticsEventNames } from 'garaje/utils/enums';

import type AttendanceOverviewController from './controller';

export interface AnalyticsAttendanceRouteParams {
  location_id: string;
}

export default class AttendanceOverviewRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare state: StateService;
  @service declare router: RouterService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare userFeatureFlags: UserFeatureFlagsService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare metrics: MetricsService;
  @service declare locations: LocationsService;

  title = 'Attendance Overview · Analytics · Envoy';

  queryParams = {
    location_id: { refreshModel: true },
  };

  afterModel(): void {
    this.#logMetrics();
  }

  setupController(controller: AttendanceOverviewController, model: void, transition: Transition): void {
    super.setupController(controller, model, transition);

    const { location_id } = <AnalyticsAttendanceRouteParams>this.paramsFor(this.routeName);
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const protectedController = this.controllerFor('protected');

    if (location_id && this.state.currentLocation.id != location_id) {
      protectedController.send('switchLocation', location_id, 'analytics.attendance.overview');

      this.locations.loadAllTask
        .perform()
        .then(() => {
          const matchingLocation = this.locations.allActive.filter((entry) => entry.id === location_id);
          if (matchingLocation.length === 0) {
            this.router.transitionTo('unauthorized');
          }
        })
        .catch((error) => {
          throw error;
        });
    }

    if (
      this.featureFlags.isEnabled('sigma-embedded-analytics') ||
      this.userFeatureFlags.isEnabled('sigma-embedded-analytics-by-user')
    ) {
      void controller.fetchSigmaUrlTask.perform();
    }
  }

  beforeModel(): void {
    if (this.abilities.cannot('visit-attendance analytic')) {
      this.router.transitionTo('unauthorized');
    }
  }

  #logMetrics(): void {
    const {
      currentCompany: { id: companyId },
      currentLocation: { id: locationId },
    } = this.state;
    const details = {
      companyId,
      locationId,
      app: this.state.workplaceSubscription?.app,
      plan: this.state?.workplaceSubscription?.plan,
      userId: this.currentAdmin?.id || '',
      employeeId: this.currentAdmin.employee?.id || '',
    };
    this.metrics.trackEvent(AnalyticsEventNames.ATTENDANCE_DASHBOARD_VISITED, details);
    this.metrics.logMonitorEvent(AnalyticsEventNames.ATTENDANCE_DASHBOARD_VISITED, details);
  }
}
